import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import Logo from "@/components/Logo";
import { Reveal, LineReveal } from "@/components/Reveal";
import { Field, TextInput, TextArea, SubmitButton } from "@/components/forms";
import { useSettings, MAPS_EMBED, AREA_DESCRIPTOR, formatAddress } from "@/hooks/SettingsContext";
import { trackPhoneClick, trackEmailClick } from "@/lib/analytics";

export default function Contact() {
  const { settings } = useSettings();
  const s = settings || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const hasTimes = s.sunday_time || s.wednesday_time;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact - Victory Chapel, Clay County FL"
        description="Contact Victory Chapel (VC Clay), a new church near CR 220 & Henley Road in the Lake Asbury / Middleburg area of Clay County, FL. Questions about services, VC Kids, or planning your visit - we'd love to hear from you."
        path="/contact"
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Contact</p>
          <LineReveal className="mt-4 font-display text-5xl font-900 tracking-tighter sm:text-7xl" lines={["LET'S TALK."]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Questions about Victory Chapel, VC Kids, or your first visit? We'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-28" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 sm:gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-3xl bg-navy p-7 text-white sm:p-10">
              <Logo variant="light" />
              <ul className="mt-8 space-y-5 text-sm text-white/80">
                <li className="flex gap-3"><MapPin size={17} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" /> {formatAddress(s) || AREA_DESCRIPTOR}</li>
                {hasTimes && (
                  <li className="flex gap-3"><Clock size={17} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" />
                    <span>{s.sunday_time}{s.sunday_time && s.wednesday_time ? <br /> : null}{s.wednesday_time}</span>
                  </li>
                )}
                {s.phone && (
                  <li className="flex gap-3"><Phone size={17} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" />
                    <a href={`tel:${s.phone.replace(/[^0-9+]/g, "")}`} onClick={trackPhoneClick} data-testid="contact-phone" className="hover:text-white">{s.phone}</a>
                  </li>
                )}
                {s.email && (
                  <li className="flex gap-3"><Mail size={17} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" />
                    <a href={`mailto:${s.email}`} onClick={trackEmailClick} data-testid="contact-email" className="hover:text-white">{s.email}</a>
                  </li>
                )}
              </ul>
              <div className="mt-8 flex gap-3">
                {s.facebook && <a href={s.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="contact-facebook" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-victory"><Facebook size={17} /></a>}
                {s.instagram && <a href={s.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="contact-instagram" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-victory"><Instagram size={17} /></a>}
                {s.youtube && <a href={s.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="contact-youtube" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-victory"><Youtube size={17} /></a>}
              </div>
              <div className="mt-8 overflow-hidden rounded-2xl">
                <iframe title="Map to Victory Chapel near CR 220 and Henley Road, Clay County FL" src={MAPS_EMBED} className="h-56 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="contact-map" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                className="flex h-full items-center justify-center rounded-3xl bg-navy p-10 text-center text-white shadow-lift" data-testid="contact-success">
                <div>
                  <h2 className="font-display text-3xl font-800 tracking-tight">MESSAGE RECEIVED.</h2>
                  <p className="mt-4 text-white/75">Thanks for reaching out - we'll be in touch soon.</p>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl bg-white p-6 shadow-lift sm:p-10" data-testid="contact-form">
                <h2 className="font-display text-2xl font-800 tracking-tight text-navy">Send us a message</h2>
                <div className="mt-6 space-y-5">
                  <Field label="Name" id="ct-name" required>
                    <TextInput id="ct-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" />
                  </Field>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Email" id="ct-email" required>
                      <TextInput id="ct-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
                    </Field>
                    <Field label="Phone" id="ct-phone">
                      <TextInput id="ct-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} autoComplete="tel" />
                    </Field>
                  </div>
                  <Field label="Message" id="ct-message" required>
                    <TextArea id="ct-message" rows={6} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  </Field>
                </div>
                <div className="mt-7">
                  <SubmitButton testId="contact-submit" loading={loading}>SEND MESSAGE</SubmitButton>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
